import { Decorative } from "@faire/design-tokens";
import { selectBadge } from "@faire/retailer-visitor-shared/components/ProductBadge/badgeUtils";
import { PerStyleVariationMapBuilder } from "@faire/retailer-visitor-shared/features/OpenSizing/PerStyleVariationMapBuilder";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { ENABLE_CONSUMER_MODE } from "@faire/retailer-visitor-shared/lib/consts";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { isProductOptionInStock } from "@faire/retailer-visitor-shared/lib/productOptions";
import { showProductPromotions } from "@faire/retailer-visitor-shared/lib/showProductPromotions";
import { LoggedOutPrice } from "@faire/retailer-visitor-shared/views/product/ProductDetails/__internal__/Header/LoggedOutPrice";
import { IMarketPromo } from "@faire/retailer-visitor-shared/views/product/ProductDetails/__internal__/Header/MarketPromoLine";
import { ProductDetailsPromotion } from "@faire/retailer-visitor-shared/views/product/ProductDetails/__internal__/Header/ProductDetailsPromotion";
import { ProductName } from "@faire/retailer-visitor-shared/views/product/ProductDetails/__internal__/Header/ProductName";
import { RedesignedOutOfStockText } from "@faire/retailer-visitor-shared/views/product/ProductDetails/__internal__/Header/RedesignedOutOfStockText";
import { IProductPromotionsData } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/interfaces";
import { ProductDetails } from "@faire/retailer-visitor-shared/views/product/ProductDetails/model";
import { Price } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Price/Price";
import { PRICE_TEXT_SURFACE } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Price/PriceProps";
import { originalPriceMoney } from "@faire/retailer-visitor-shared/views/product/ProductDetails/productOriginalPrice";
import { productRetailPrice } from "@faire/retailer-visitor-shared/views/product/ProductDetails/productRetailPrice";
import { productWholesalePrice } from "@faire/retailer-visitor-shared/views/product/ProductDetails/productWholesalePrice";
import { MaybeSimpleBrandTitle } from "@faire/retailer-visitor-shared/views/product/ProductDetails/SimpleBrandTitle";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Storage } from "@faire/web--source/common/Storage";
import { useTraceComponentRender } from "@faire/web--source/common/tracing/useTraceComponentRender";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { IBadge } from "@faire/web-api--source/indigofair/badges/IBadge";
import { IProductOption } from "@faire/web-api--source/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import * as React from "react";
import styled from "styled-components";

import { IBrandReviewSummary } from "../hooks/interfaces";

export interface IProductDetailsHeaderProps {
  brand?: IProductPageResponse.IPdpBrand;
  product: ProductDetails.IProduct;
  badges?: IBadge[];
  selectedOption?: IProductOption;
  isQuickAddModal?: boolean;
  centerTitle?: boolean;
  productPromotionsState?: IProductPromotionsData;
  perStyleOptionVariations?: PerStyleVariationMapBuilder.IPerStyleOptionVariation[];
  hideBrandDetails?: boolean;
  seperateLinePrice?: boolean;
  brandPromotion?: IMarketPromo;
  showOutOfStockUnderProductName: boolean;
  surface?: PRICE_TEXT_SURFACE;
  brandReviewSummary: IBrandReviewSummary;
  isDesktopAndAbove: boolean;
}

export const ProductDetailsHeader: React.FC<IProductDetailsHeaderProps> = ({
  product,
  selectedOption,
  perStyleOptionVariations,
  badges,
  hideBrandDetails,
  isQuickAddModal,
  brand,
  centerTitle,
  productPromotionsState,
  brandPromotion,
  showOutOfStockUnderProductName,
  surface,
  brandReviewSummary,
  isDesktopAndAbove,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const isOutOfStock =
    selectedOption !== undefined &&
    !isProductOptionInStock(selectedOption, perStyleOptionVariations);
  const wholesalePrice = productWholesalePrice(
    strictLocalize,
    product,
    false,
    selectedOption,
    perStyleOptionVariations
  );
  const retailPrice = productRetailPrice(selectedOption);
  const originalPrice = originalPriceMoney(selectedOption);
  const badge = selectBadge(badges ?? [], "TOP");
  const { isUK } = useNationality();

  const shouldShowProductPromotions =
    showProductPromotions(selectedOption?.wholesale_promo_price) &&
    !isOutOfStock;

  const isSingleOption = product.options.length === 1;

  const isConsumerMode =
    Storage.session.getItem(ENABLE_CONSUMER_MODE) === "true" &&
    !isLoggedInRetailerOrBrandPreview();

  useTraceComponentRender("ProductDetailsHeader");

  return (
    <Wrapper
      $isQuickAddModal={isQuickAddModal}
      $isSingleOption={isSingleOption}
      data-test-id="product-detail-header"
    >
      {hideBrandDetails ? null : (
        <>
          <MaybeSimpleBrandTitle
            brand={brand}
            product={product}
            brandReviewSummary={brandReviewSummary}
            isDesktopAndAbove={isDesktopAndAbove}
          />
          <Spacer height="3x" />
        </>
      )}
      {badge ? (
        <>
          <Typography
            variant="paragraphSansMedium"
            color={Decorative.shade.orange}
          >
            {badge.style?.badge_message}
          </Typography>
          <Spacer size={4} />
        </>
      ) : null}
      <ProductName
        brand={brand}
        isQuickAddModal={isQuickAddModal}
        centerTitle={centerTitle}
        product={product}
      />

      {isLoggedInRetailerOrBrandPreview() ? (
        <>
          <RedesignedOutOfStockText
            isOutOfStock={isOutOfStock}
            showOutOfStock={showOutOfStockUnderProductName}
            dataTestId="out-of-stock-text-in-Header"
          />
          <Spacer size="2x" />
          <Price
            isUKRetailer={isUK}
            showProductPromotions={shouldShowProductPromotions}
            originalPrice={originalPrice}
            wholesalePrice={wholesalePrice ?? ""}
            retailPrice={retailPrice}
            isLoggedInOrBrandPreview
            isOutOfStock={isOutOfStock}
            showOutOfStockUnderProductName={showOutOfStockUnderProductName}
            priceTextSurface={surface ?? PRICE_TEXT_SURFACE.PDP}
          />
          <ProductDetailsPromotion
            brandPromotion={brandPromotion}
            brand={brand}
            productPromotionsState={productPromotionsState}
            product={product}
          />
        </>
      ) : (
        <LoggedOutPrice retailPrice={retailPrice} showWSP={!isConsumerMode} />
      )}
    </Wrapper>
  );
};

// eslint-disable-next-line @faire/prefer-slate-attrs -- move align to a prop
const Wrapper = styled(Column)<{
  $isQuickAddModal?: boolean;
  $isSingleOption?: boolean;
}>`
  align-items: ${(props) => {
    if (props.$isQuickAddModal) {
      return props.$isSingleOption ? "center" : "stretch";
    } else {
      return "flex-start";
    }
  }};
  margin: 0;
  width: 100%;
`;
